import { AxiosError } from 'axios';

// i18n API 에러 타입 정의
export interface I18nApiError {
  field?: string;
  language?: string;
  message: string;
  code?: string;
}

export interface I18nValidationError {
  [field: string]: {
    [language: string]: string[];
  };
}

// i18n 관련 에러 메시지
export const I18N_ERROR_MESSAGES = {
  REQUIRED_KOREAN: 'Korean is a required field',
  INVALID_LANGUAGE: (lang: string) => `Unsupported language code: ${lang}`,
  MAX_LENGTH_EXCEEDED: (field: string, maxLength: number) =>
    `${field} field allows a maximum of ${maxLength} characters`,
  SYNC_ERROR: 'Failed to synchronize legacy fields and i18n fields',
  VALIDATION_ERROR: 'Multilingual data validation failed',
  NETWORK_ERROR: 'A network error occurred',
  SERVER_ERROR: 'A server error occurred',
  UNAUTHORIZED: 'Authentication is required',
  FORBIDDEN: 'Access denied',
  NOT_FOUND: 'The requested content was not found',
  CONFLICT: 'A data conflict occurred',
} as const;

/**
 * i18n API 에러 처리 클래스
 */
export class I18nApiErrorHandler {
  /**
   * Axios 에러를 i18n 에러로 변환
   */
  static handleAxiosError(error: AxiosError): I18nApiError[] {
    if (!error.response) {
      return [
        {
          message: I18N_ERROR_MESSAGES.NETWORK_ERROR,
          code: 'NETWORK_ERROR',
        },
      ];
    }

    const { status, data } = error.response;

    switch (status) {
      case 400:
        return this.handleValidationError(data);
      case 401:
        return [
          {
            message: I18N_ERROR_MESSAGES.UNAUTHORIZED,
            code: 'UNAUTHORIZED',
          },
        ];
      case 403:
        return [
          {
            message: I18N_ERROR_MESSAGES.FORBIDDEN,
            code: 'FORBIDDEN',
          },
        ];
      case 404:
        return [
          {
            message: I18N_ERROR_MESSAGES.NOT_FOUND,
            code: 'NOT_FOUND',
          },
        ];
      case 409:
        return [
          {
            message: I18N_ERROR_MESSAGES.CONFLICT,
            code: 'CONFLICT',
          },
        ];
      case 500:
      default:
        return [
          {
            message: I18N_ERROR_MESSAGES.SERVER_ERROR,
            code: 'SERVER_ERROR',
          },
        ];
    }
  }

  /**
   * 검증 에러 처리 (400 에러)
   */
  private static handleValidationError(data: any): I18nApiError[] {
    const errors: I18nApiError[] = [];

    if (data && typeof data === 'object') {
      // i18n 필드 검증 에러 처리
      Object.keys(data).forEach(field => {
        if (field.startsWith('i18n_')) {
          const i18nErrors = data[field];
          if (typeof i18nErrors === 'object') {
            Object.keys(i18nErrors).forEach(language => {
              const languageErrors = i18nErrors[language];
              if (Array.isArray(languageErrors)) {
                languageErrors.forEach(message => {
                  errors.push({
                    field,
                    language,
                    message: this.translateValidationMessage(message, field, language),
                    code: 'VALIDATION_ERROR',
                  });
                });
              }
            });
          }
        } else {
          // 레거시 필드 검증 에러 처리
          const fieldErrors = data[field];
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach(message => {
              errors.push({
                field,
                message: this.translateValidationMessage(message, field),
                code: 'VALIDATION_ERROR',
              });
            });
          }
        }
      });
    }

    if (errors.length === 0) {
      errors.push({
        message: I18N_ERROR_MESSAGES.VALIDATION_ERROR,
        code: 'VALIDATION_ERROR',
      });
    }

    return errors;
  }

  /**
   * 검증 메시지를 한국어로 번역
   */
  private static translateValidationMessage(
    message: string,
    field?: string,
    language?: string
  ): string {
    // 공통 검증 메시지 번역
    if (message.includes('required') || message.includes('required')) {
      if (language === 'ko') {
        return I18N_ERROR_MESSAGES.REQUIRED_KOREAN;
      }
      return `${field} field is required`;
    }

    if (message.includes('max_length') || message.includes('max')) {
      const maxLengthMatch = message.match(/(\d+)/);
      const maxLength = maxLengthMatch ? parseInt(maxLengthMatch[1]) : 255;
      return I18N_ERROR_MESSAGES.MAX_LENGTH_EXCEEDED(field || 'field', maxLength);
    }

    if (message.includes('invalid')) {
      if (language) {
        return I18N_ERROR_MESSAGES.INVALID_LANGUAGE(language);
      }
      return `The value for ${field} field is invalid`;
    }

    // 원본 메시지 반환
    return message;
  }

  /**
   * 에러 메시지를 사용자 친화적인 형태로 포맷
   */
  static formatErrorsForUser(errors: I18nApiError[]): string {
    if (errors.length === 0) {
      return I18N_ERROR_MESSAGES.VALIDATION_ERROR;
    }

    if (errors.length === 1) {
      return errors[0].message;
    }

    // 여러 에러가 있는 경우 목록으로 표시
    const errorMessages = errors.map((error, index) => {
      let message = `${index + 1}. ${error.message}`;
      if (error.field && error.language) {
        message += ` (${error.field} - ${error.language})`;
      } else if (error.field) {
        message += ` (${error.field})`;
      }
      return message;
    });

    return errorMessages.join('\n');
  }

  /**
   * 필드별 에러 그룹화
   */
  static groupErrorsByField(errors: I18nApiError[]): Record<string, I18nApiError[]> {
    return errors.reduce(
      (groups, error) => {
        const key = error.field || 'general';
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(error);
        return groups;
      },
      {} as Record<string, I18nApiError[]>
    );
  }

  /**
   * 언어별 에러 그룹화
   */
  static groupErrorsByLanguage(errors: I18nApiError[]): Record<string, I18nApiError[]> {
    return errors.reduce(
      (groups, error) => {
        const key = error.language || 'general';
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(error);
        return groups;
      },
      {} as Record<string, I18nApiError[]>
    );
  }
}
